import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  addClinicalNote,
  updateClinicalNote,
  deleteClinicalNote,
  fetchClinicalNotes,
} from '../features/patients/patientSlice';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Assessment as AssessmentIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Science as LabIcon,
  Chat as ChatIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: theme.palette.background.paper,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
}));

const noteTypes = [
  {
    value: 'initial_assessment',
    label: 'Initial Assessment',
    icon: <AssessmentIcon />,
  },
  { value: 'progress_note', label: 'Progress Note', icon: <NoteIcon /> },
  { value: 'treatment_plan', label: 'Treatment Plan', icon: <HospitalIcon /> },
  {
    value: 'medication_review',
    label: 'Medication Review',
    icon: <MedicationIcon />,
  },
  { value: 'lab_results', label: 'Lab Results', icon: <LabIcon /> },
  { value: 'consultation', label: 'Consultation', icon: <ChatIcon /> },
  { value: 'other', label: 'Other', icon: <DescriptionIcon /> },
];

const ClinicalNotes = ({ patientId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const clinicalNotes = patientsState?.clinicalNotes || null;
  const loading = patientsState?.clinicalNotes?.loading || false;
  const error = patientsState?.clinicalNotes?.error || null;
  
  // Flatten clinical notes from different categories
  const allClinicalNotes = useMemo(() => {
    if (!clinicalNotes) return [];
    return [
      ...(clinicalNotes.mse || []),
      ...(clinicalNotes.progressNotes || []),
      ...(clinicalNotes.dischargeSummaries || [])
    ];
  }, [clinicalNotes]);
  
  // Debug logging
  useEffect(() => {
    console.log('📝 Clinical Notes State:', {
      hasClinicalNotes: !!clinicalNotes,
      mseCount: clinicalNotes?.mse?.length || 0,
      progressNotesCount: clinicalNotes?.progressNotes?.length || 0,
      dischargeSummariesCount: clinicalNotes?.dischargeSummaries?.length || 0,
      totalNotes: allClinicalNotes.length,
      loading,
      error
    });
  }, [clinicalNotes, allClinicalNotes.length, loading, error]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general',
    date: '',
    clinician: '',
    diagnosis: '',
    treatment: '',
    followUp: '',
    admissionDate: '',
    dischargeDate: '',
    reasonForAdmission: '',
    treatmentProvided: '',
    dischargeDiagnosis: '',
    followUpInstructions: '',
    dischargePlan: '',
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    treatmentProgress: '',
    nextSteps: '',
    followUpDate: '',
    appearance: '',
    behavior: '',
    speech: '',
    mood: '',
    affect: '',
    thoughtProcess: '',
    thoughtContent: '',
    cognition: '',
    insight: '',
    judgment: '',
    suicidalIdeation: false,
    homicidalIdeation: false,
    hallucinations: false,
    delusions: false,
    orientation: {
      time: false,
      place: false,
      person: false,
      situation: false,
    },
  });

  useEffect(() => {
    if (patientId) {
      dispatch(fetchClinicalNotes(patientId));
    }
  }, [dispatch, patientId]);

  const handleOpenDialog = (note = null) => {
    if (note) {
      setSelectedNote(note);
      setFormData({
        title: note.title || '',
        content: note.content || '',
        type: note.type || 'general',
        date: note.created_at
          ? format(new Date(note.created_at), 'yyyy-MM-dd')
          : '',
        clinician: note.clinician || '',
        diagnosis: note.diagnosis || '',
        treatment: note.treatment || '',
        followUp: note.followUp || '',
        admissionDate: note.admissionDate
          ? format(new Date(note.admissionDate), 'yyyy-MM-dd')
          : '',
        dischargeDate: note.dischargeDate
          ? format(new Date(note.dischargeDate), 'yyyy-MM-dd')
          : '',
        reasonForAdmission: note.reasonForAdmission || '',
        treatmentProvided: note.treatmentProvided || '',
        dischargeDiagnosis: note.dischargeDiagnosis || '',
        followUpInstructions: note.followUpInstructions || '',
        dischargePlan: note.dischargePlan || '',
        subjective: note.subjective || '',
        objective: note.objective || '',
        assessment: note.assessment || '',
        plan: note.plan || '',
        treatmentProgress: note.treatmentProgress || '',
        nextSteps: note.nextSteps || '',
        followUpDate: note.followUpDate
          ? format(new Date(note.followUpDate), 'yyyy-MM-dd')
          : '',
        appearance: note.appearance || '',
        behavior: note.behavior || '',
        speech: note.speech || '',
        mood: note.mood || '',
        affect: note.affect || '',
        thoughtProcess: note.thoughtProcess || '',
        thoughtContent: note.thoughtContent || '',
        cognition: note.cognition || '',
        insight: note.insight || '',
        judgment: note.judgment || '',
        suicidalIdeation: note.suicidalIdeation || false,
        homicidalIdeation: note.homicidalIdeation || false,
        hallucinations: note.hallucinations || false,
        delusions: note.delusions || false,
        orientation: {
          time: note.orientation?.time || false,
          place: note.orientation?.place || false,
          person: note.orientation?.person || false,
          situation: note.orientation?.situation || false,
        },
      });
    } else {
      setSelectedNote(null);
      setFormData({
        title: '',
        content: '',
        type: 'general',
        date: '',
        clinician: '',
        diagnosis: '',
        treatment: '',
        followUp: '',
        admissionDate: '',
        dischargeDate: '',
        reasonForAdmission: '',
        treatmentProvided: '',
        dischargeDiagnosis: '',
        followUpInstructions: '',
        dischargePlan: '',
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        treatmentProgress: '',
        nextSteps: '',
        followUpDate: '',
        appearance: '',
        behavior: '',
        speech: '',
        mood: '',
        affect: '',
        thoughtProcess: '',
        thoughtContent: '',
        cognition: '',
        insight: '',
        judgment: '',
        suicidalIdeation: false,
        homicidalIdeation: false,
        hallucinations: false,
        delusions: false,
        orientation: {
          time: false,
          place: false,
          person: false,
          situation: false,
        },
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNote(null);
    setFormData({
      title: '',
      content: '',
      type: 'general',
      date: '',
      clinician: '',
      diagnosis: '',
      treatment: '',
      followUp: '',
      admissionDate: '',
      dischargeDate: '',
      reasonForAdmission: '',
      treatmentProvided: '',
      dischargeDiagnosis: '',
      followUpInstructions: '',
      dischargePlan: '',
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      treatmentProgress: '',
      nextSteps: '',
      followUpDate: '',
      appearance: '',
      behavior: '',
      speech: '',
      mood: '',
      affect: '',
      thoughtProcess: '',
      thoughtContent: '',
      cognition: '',
      insight: '',
      judgment: '',
      suicidalIdeation: false,
      homicidalIdeation: false,
      hallucinations: false,
      delusions: false,
      orientation: {
        time: false,
        place: false,
        person: false,
        situation: false,
      },
    });
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!patientId) {
      alert('Patient ID is required');
      return;
    }

    try {
      const noteType = activeTab === 0 ? 'mse' : activeTab === 1 ? 'progress_note' : 'discharge_summary';
      const noteData = {
        ...formData,
        type: noteType,
        patient_id: patientId,
      };

      if (selectedNote) {
        await dispatch(
          updateClinicalNote({
            patientId,
            noteId: selectedNote.id,
            noteData,
          })
        ).unwrap();
      } else {
        await dispatch(addClinicalNote({ patientId, noteData })).unwrap();
      }

      // Refresh clinical notes after adding/updating
      await dispatch(fetchClinicalNotes(patientId));
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving clinical note:', error);
      alert(`Failed to save note: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      await dispatch(deleteClinicalNote({ patientId, noteId })).unwrap();
      // Refresh clinical notes after deletion
      await dispatch(fetchClinicalNotes(patientId));
    } catch (error) {
      console.error('Error deleting clinical note:', error);
      alert(`Failed to delete note: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  };

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'general':
        return 'default';
      case 'symptom':
        return 'error';
      case 'diagnosis':
        return 'primary';
      case 'treatment':
        return 'success';
      default:
        return 'default';
    }
  };

  const getNoteTypeLabel = (type) => {
    switch (type) {
      case 'mse':
        return 'Mental Status Exam';
      case 'progress':
        return 'Progress Note';
      case 'discharge':
        return 'Discharge Summary';
      default:
        return type;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFormData((prev) => ({
      ...prev,
      type: ['mse', 'progress', 'discharge'][newValue],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOrientationChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      orientation: {
        ...prev.orientation,
        [field]: !prev.orientation[field],
      },
    }));
  };

  const renderMSETab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Date'
          type='date'
          name='date'
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Clinician'
          name='clinician'
          value={formData.clinician}
          onChange={handleChange}
        />
      </Grid>

      {/* MSE Fields */}
      {[
        'appearance',
        'behavior',
        'speech',
        'mood',
        'affect',
        'thoughtProcess',
        'thoughtContent',
        'cognition',
        'insight',
        'judgment',
      ].map((field) => (
        <Grid item xs={12} md={6} key={field}>
          <TextField
            fullWidth
            label={field.charAt(0).toUpperCase() + field.slice(1)}
            name={field}
            value={formData[field]}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      ))}

      {/* Orientation */}
      <Grid item xs={12}>
        <Typography variant='subtitle2' gutterBottom>
          Orientation
        </Typography>
        <FormGroup row>
          {['time', 'place', 'person', 'situation'].map((field) => (
            <FormControlLabel
              key={field}
              control={
                <Checkbox
                  checked={formData.orientation[field]}
                  onChange={() => handleOrientationChange(field)}
                />
              }
              label={field.charAt(0).toUpperCase() + field.slice(1)}
            />
          ))}
        </FormGroup>
      </Grid>

      {/* Risk Assessment */}
      <Grid item xs={12}>
        <Typography variant='subtitle2' gutterBottom>
          Risk Assessment
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.suicidalIdeation}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'suicidalIdeation',
                      value: e.target.checked,
                    },
                  })
                }
              />
            }
            label='Suicidal Ideation'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.homicidalIdeation}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'homicidalIdeation',
                      value: e.target.checked,
                    },
                  })
                }
              />
            }
            label='Homicidal Ideation'
          />
        </FormGroup>
      </Grid>

      {/* Psychotic Symptoms */}
      <Grid item xs={12}>
        <Typography variant='subtitle2' gutterBottom>
          Psychotic Symptoms
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hallucinations}
                onChange={(e) =>
                  handleChange({
                    target: { name: 'hallucinations', value: e.target.checked },
                  })
                }
              />
            }
            label='Hallucinations'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.delusions}
                onChange={(e) =>
                  handleChange({
                    target: { name: 'delusions', value: e.target.checked },
                  })
                }
              />
            }
            label='Delusions'
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  const renderProgressNoteTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Date'
          type='date'
          name='date'
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Clinician'
          name='clinician'
          value={formData.clinician}
          onChange={handleChange}
        />
      </Grid>

      {/* SOAP Format */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Subjective'
          name='subjective'
          value={formData.subjective}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Objective'
          name='objective'
          value={formData.objective}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Assessment'
          name='assessment'
          value={formData.assessment}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Plan'
          name='plan'
          value={formData.plan}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>

      {/* Treatment Progress */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Treatment Progress'
          name='treatmentProgress'
          value={formData.treatmentProgress}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Next Steps'
          name='nextSteps'
          value={formData.nextSteps}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Follow-up Date'
          type='date'
          name='followUpDate'
          value={formData.followUpDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );

  const renderDischargeSummaryTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Admission Date'
          type='date'
          name='admissionDate'
          value={formData.admissionDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label='Discharge Date'
          type='date'
          name='dischargeDate'
          value={formData.dischargeDate}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Reason for Admission'
          name='reasonForAdmission'
          value={formData.reasonForAdmission}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Treatment Provided'
          name='treatmentProvided'
          value={formData.treatmentProvided}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Discharge Diagnosis'
          name='dischargeDiagnosis'
          value={formData.dischargeDiagnosis}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Follow-up Instructions'
          name='followUpInstructions'
          value={formData.followUpInstructions}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Discharge Plan'
          name='dischargePlan'
          value={formData.dischargePlan}
          onChange={handleChange}
          multiline
          rows={4}
        />
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h5'>Clinical Notes</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Note
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <List>
          {allClinicalNotes.map((note) => (
            <ListItem key={note.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='subtitle1'>{note.title}</Typography>
                    <Chip
                      label={getNoteTypeLabel(note.type)}
                      color={getNoteTypeColor(note.type)}
                      size='small'
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant='body2' color='text.secondary'>
                      {new Date(note.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      {note.content.substring(0, 150)}
                      {note.content.length > 150 && '...'}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge='end'
                  aria-label='edit'
                  onClick={() => handleOpenDialog(note)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge='end'
                  aria-label='delete'
                  onClick={() => handleDelete(note.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Clinical Notes
        </Typography>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label='Mental Status Exam' />
          <Tab label='Progress Note' />
          <Tab label='Discharge Summary' />
        </Tabs>

        <form onSubmit={handleSubmit}>
          {activeTab === 0 && renderMSETab()}
          {activeTab === 1 && renderProgressNoteTab()}
          {activeTab === 2 && renderDischargeSummaryTab()}

          <Box sx={{ mt: 3 }}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              fullWidth
              size='large'
            >
              Submit Note
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selectedNote ? 'Edit Clinical Note' : 'Add Clinical Note'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Note Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  label='Note Type'
                >
                  <MenuItem value='mse'>Mental Status Exam</MenuItem>
                  <MenuItem value='progress'>Progress Note</MenuItem>
                  <MenuItem value='discharge'>Discharge Summary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Title'
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label='Content'
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label='Diagnosis'
                value={formData.diagnosis}
                onChange={(e) =>
                  setFormData({ ...formData, diagnosis: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label='Treatment'
                value={formData.treatment}
                onChange={(e) =>
                  setFormData({ ...formData, treatment: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label='Follow-up Plan'
                value={formData.followUp}
                onChange={(e) =>
                  setFormData({ ...formData, followUp: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained' disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicalNotes;
